import { cn } from '@/lib/utils';
import { testIds } from '@/lib/test-ids';
import { formatCorrectAnswerFeedback } from '@/lib/quiz-option-shuffle';
import { Check } from 'lucide-react';

export type ChoiceOption = {
  id: string;
  label: string;
  description?: string;
};

function optionDisplayLetter(id: string, index: number): string {
  const key = String(id || '').toLowerCase();
  if (/^[a-d]$/.test(key)) return key.toUpperCase();
  return String(index + 1);
}

export type MultipleChoiceFormProps = {
  question?: string;
  options: ChoiceOption[];
  selectionMode?: 'single' | 'multiple';
  value?: string[];
  revealed?: {
    correctIds?: string[];
    explanation?: string;
  };
  disabled?: boolean;
  /** Instant single-select (CTA / quiz-grade). Ignored when selectionMode is multiple. */
  onOptionActivate?: (id: string) => void;
  onChange?: (selectedIds: string[]) => void;
  className?: string;
  /** Visual density: quiz rows (full width) vs compact chat CTAs */
  density?: 'comfortable' | 'compact';
};

/**
 * Presentational multiple-choice form shared by quiz Practice, flash cards, and plan AskQuestion.
 */
export function MultipleChoiceForm({
  question,
  options,
  selectionMode = 'single',
  value = [],
  revealed,
  disabled = false,
  onOptionActivate,
  onChange,
  className,
  density = 'comfortable',
}: MultipleChoiceFormProps) {
  const selected = new Set(value);
  const correctIds = new Set(revealed?.correctIds || []);
  const isRevealed = !!revealed;
  const primaryCorrectId = revealed?.correctIds?.[0];
  const primaryCorrectOpt = primaryCorrectId
    ? options.find((o) => o.id === primaryCorrectId)
    : undefined;
  const correctAnswerLine =
    isRevealed && primaryCorrectId
      ? formatCorrectAnswerFeedback(primaryCorrectId, primaryCorrectOpt?.label)
      : null;

  const toggle = (id: string) => {
    if (disabled || isRevealed) return;
    if (selectionMode === 'single') {
      onOptionActivate?.(id);
      onChange?.([id]);
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.(Array.from(next));
  };

  return (
    <div
      className={cn('space-y-3', className)}
      data-testid={testIds.multipleChoiceForm.root}
    >
      {question ? (
        <p
          className={cn(
            'font-medium leading-relaxed text-foreground',
            density === 'compact' ? 'text-sm' : 'text-base'
          )}
          data-testid={testIds.multipleChoiceForm.question}
        >
          {question}
        </p>
      ) : null}

      <div
        className="space-y-2"
        role={selectionMode === 'multiple' ? 'group' : 'radiogroup'}
        aria-label={question || 'Choices'}
      >
        {options.map((opt, i) => {
          const isSelected = selected.has(opt.id);
          const isCorrect = correctIds.has(opt.id);
          const showIncorrect = isRevealed && isSelected && !isCorrect;
          const showCorrect = isRevealed && isCorrect;

          return (
            <button
              key={opt.id}
              type="button"
              disabled={disabled || isRevealed}
              onClick={() => toggle(opt.id)}
              data-testid={testIds.multipleChoiceForm.option(opt.id)}
              role={selectionMode === 'multiple' ? 'checkbox' : 'radio'}
              aria-checked={isSelected}
              className={cn(
                'w-full text-left rounded-md border transition-colors',
                density === 'compact' ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm',
                !isRevealed &&
                  (isSelected
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border hover:bg-muted/50'),
                showCorrect && 'border-green-500/50 bg-green-500/10 text-foreground',
                showIncorrect && 'border-red-500/50 bg-red-500/10 text-foreground',
                (disabled || isRevealed) && 'cursor-default opacity-95'
              )}
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0">
                  {selectionMode === 'multiple' ? (
                    <span
                      aria-hidden
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/50 bg-background',
                      )}
                    >
                      {isSelected ? <Check className="h-3 w-3" /> : null}
                    </span>
                  ) : (
                    <span className="font-mono text-muted-foreground">
                      {optionDisplayLetter(opt.id, i)}.
                    </span>
                  )}
                </span>
                <span className="min-w-0">
                  <span className="font-medium">{opt.label}</span>
                  {opt.description ? (
                    <span className="block text-muted-foreground mt-0.5 font-normal">
                      {opt.description}
                    </span>
                  ) : null}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {correctAnswerLine ? (
        <p
          className={cn(
            'font-medium text-foreground',
            density === 'comfortable' ? 'text-sm' : 'text-sm'
          )}
          data-testid={testIds.multipleChoiceForm.correctAnswer}
        >
          {correctAnswerLine}
        </p>
      ) : null}

      {revealed?.explanation ? (
        <p
          className={cn(
            'text-muted-foreground',
            density === 'comfortable'
              ? 'text-sm leading-relaxed rounded-md bg-muted/50 border border-border/60 px-3 py-2.5'
              : 'text-sm border-l-2 border-border pl-3'
          )}
          data-testid={testIds.multipleChoiceForm.explanation}
        >
          {revealed.explanation}
        </p>
      ) : null}
    </div>
  );
}
